<template>
	<div v-if="!loading">
		<vx-card>
			<template slot="header">
				<div class="flex justify-between items-center w-full">
					<h2 class="card-title">Producten</h2>
				</div>
			</template>

			<div class="mb-5">
				<div class="vx-row">
					<div class="vx-col lg:w-1/4 md:w-1/2 w-full">
						<vs-input
							label="Zoeken in producten"
							v-model="searchQuery"
							@input="performSearch"
							placeholder="EAN, titel..."
						></vs-input>
					</div>
				</div>
			</div>

			<!-- Products Grid -->
			<div class="vx-row">
				<div
					v-for="product in products"
					:key="product.id"
					class="vx-col lg:w-1/4 md:w-1/2 w-full mb-5"
				>
					<div class="product-card">
						<div class="product-card-header">
							<h3 class="product-title">{{ product.title }}</h3>
							<div class="product-ean">{{ product.ean }}</div>
						</div>

						<div class="product-card-body">
							<div class="product-info-row">
								<span class="label">Voorraad:</span>
								<span :class="getStockClass(product)" class="value">
									{{ product.stock }}
								</span>
							</div>

							<div class="product-info-row">
								<span class="label">Locatie:</span>
								<span class="value">{{ product.location || '-' }}</span>
							</div>

							<div class="product-info-row">
								<span class="label">Prijs:</span>
								<span class="value">€ {{ product.price }}</span>
							</div>
						</div>

						<div class="product-card-footer">
							<div class="action-buttons">
								<vx-tooltip v-if="product.buy_url" text="Product inkopen">
									<button
										@click="buyProduct(product.buy_url)"
										class="action-btn action-btn-shop"
										title="Product inkopen"
									>
										<font-awesome-icon icon="shopping-cart" />
									</button>
								</vx-tooltip>

								<vx-tooltip text="Product bewerken">
									<button
										@click="openUpdatePopup(product)"
										class="action-btn action-btn-edit"
										title="Product bewerken"
									>
										<font-awesome-icon icon="edit" />
									</button>
								</vx-tooltip>

								<vx-tooltip
									v-if="!product.is_syncing"
									text="Synchroniseer product"
								>
									<button
										@click="syncAndRefresh(product.id)"
										class="action-btn action-btn-sync"
										title="Synchroniseer"
									>
										<font-awesome-icon icon="sync-alt" />
									</button>
								</vx-tooltip>

								<vx-tooltip
									v-if="product.is_syncing"
									text="Product wordt gesynchroniseerd"
								>
									<button
										class="action-btn action-btn-syncing"
										disabled
									>
										<font-awesome-icon icon="sync-alt" class="fa-spin" />
									</button>
								</vx-tooltip>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Empty State -->
			<div v-if="products.length === 0" class="empty-state">
				<div class="empty-state-icon">
					<font-awesome-icon icon="box" />
				</div>
				<p class="empty-state-text">Geen producten gevonden</p>
			</div>

			<!-- Pagination -->
			<div v-if="products.length > 0" class="pagination-container">
				<div class="pagination-controls">
					<vue-multiselect
						v-model="selectedPageSize"
						:options="pageSizeOptions"
						:show-labels="false"
						:allow-empty="false"
						class="page-size-selector"
						@input="changePageSize"
					/>
				</div>
				<vs-pagination
					:total="maxPages"
					v-model="currentPageLocal"
					@input="handlePageChange"
				></vs-pagination>
			</div>
		</vx-card>

		<update-popup ref="updatePopup" />
	</div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import updatePopup from './updatePopup/popup';

export default {
	name: 'products',
	components: {
		updatePopup
	},
	data() {
		return {
			loading: true,
			searchQuery: '',
			searchTimer: null,
			selectedPageSize: 20,
			pageSizeOptions: [10, 20, 50, 100],
			currentPageLocal: 1,
		};
	},
	computed: {
		...mapGetters({
			products: 'productStore/products',
			maxPages: 'productStore/maxPages',
			currentPage: 'productStore/currentPage',
			pageSize: 'productStore/page_size',
			search: 'productStore/search',
		}),
	},
	watch: {
		currentPage(newPage) {
			this.currentPageLocal = newPage;
		},
	},
	mounted() {
		this.$vs.loading();
		this.selectedPageSize = this.pageSize;
		this.currentPageLocal = this.currentPage;

		this.getPackages({ pageSize: 200 });
		this.getProducts().then(() => {
			this.loading = false;
			this.$vs.loading.close();
		}).catch(() => {
			this.loading = false;
			this.$vs.loading.close();
		});
	},
	methods: {
		...mapActions({
			getProducts: 'productStore/getProducts',
			getPackages: 'packages/getPackages',
			syncProducts: 'productStore/syncProducts',
		}),
		...mapMutations({
			setSearch: 'productStore/SET_SEARCH',
			setPageSize: 'productStore/SET_PAGE_SIZE',
			setCurrentPage: 'productStore/SET_CURRENT_PAGE',
			setSelectedProduct: 'productStore/SET_SELECTED_PRODUCT',
		}),
		performSearch() {
			if (this.searchTimer) {
				clearTimeout(this.searchTimer);
			}

			this.searchTimer = setTimeout(() => {
				if (this.searchQuery.length >= 2) {
					this.setSearch(this.searchQuery);
				} else if (this.searchQuery.length === 0) {
					this.setSearch(null);
				}
				this.setCurrentPage(1);
				this.getProducts();
			}, 800);
		},
		changePageSize() {
			if (this.selectedPageSize > 200) {
				this.selectedPageSize = 200;
				this.$vs.notify({
					title: 'Error',
					text: 'Paginagrootte kan maximaal 200 zijn',
					color: 'danger'
				});
			} else {
				this.setPageSize(this.selectedPageSize);
				this.setCurrentPage(1);
				this.getProducts();
			}
		},
		handlePageChange(page) {
			this.setCurrentPage(page);
			this.getProducts();
		},
		getStockClass(product) {
			if (product.stock === 0 || product.stock <= product.stock_threshold) {
				return 'stock-danger';
			} else if (product.stock_threshold && product.stock < product.stock_threshold) {
				return 'stock-warning';
			}
			return 'stock-normal';
		},
		buyProduct(url) {
			window.open(url, '_blank');
		},
		openUpdatePopup(product) {
			this.setSelectedProduct(product);
			this.$refs.updatePopup.openPopup();
		},
		syncAndRefresh(productId) {
			this.syncProducts(productId).then(() => {
				this.getProducts();
			});
		}
	}
}
</script>

<style scoped>
.product-card {
	background: white;
	border: 1px solid #e8e8e8;
	border-radius: 8px;
	overflow: hidden;
	transition: all 0.3s ease;
	display: flex;
	flex-direction: column;
	height: 100%;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.product-card:hover {
	border-color: #d8d8d8;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
	transform: translateY(-2px);
}

.product-card-header {
	padding: 16px;
	border-bottom: 1px solid #f0f0f0;
	background: linear-gradient(135deg, #f5f7fa 0%, #fafbfc 100%);
}

.product-title {
	margin: 0;
	font-size: 14px;
	font-weight: 600;
	color: #000;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	margin-bottom: 8px;
}

.product-ean {
	font-size: 12px;
	color: #666;
	font-family: monospace;
	letter-spacing: 0.5px;
}

.product-card-body {
	padding: 16px;
	flex: 1;
}

.product-info-row {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 12px;
	font-size: 13px;
}

.product-info-row:last-child {
	margin-bottom: 0;
}

.product-info-row .label {
	color: #666;
	font-weight: 500;
}

.product-info-row .value {
	color: #333;
	font-weight: 600;
}

.stock-normal {
	color: #28a745;
}

.stock-warning {
	color: #ffc107;
}

.stock-danger {
	color: #dc3545;
}

.product-card-footer {
	padding: 12px 16px;
	border-top: 1px solid #f0f0f0;
	background: #fafbfc;
}

.action-buttons {
	display: flex;
	gap: 8px;
	justify-content: flex-end;
}

.action-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border: 1px solid #ddd;
	border-radius: 6px;
	background: white;
	cursor: pointer;
	transition: all 0.2s ease;
	color: #666;
	font-size: 14px;
}

.action-btn:hover:not(:disabled) {
	border-color: #3498db;
	background: #e3f2fd;
	color: #3498db;
	transform: scale(1.05);
}

.action-btn:disabled {
	opacity: 0.6;
	cursor: not-allowed;
}

.action-btn-shop:hover {
	border-color: #27ae60;
	background: #d4edda;
	color: #27ae60;
}

.action-btn-edit:hover {
	border-color: #3498db;
	background: #e3f2fd;
	color: #3498db;
}

.action-btn-sync:hover {
	border-color: #f39c12;
	background: #fff3cd;
	color: #f39c12;
}

.action-btn-syncing {
	border-color: #adb5bd;
	background: #e9ecef;
	color: #6c757d;
}

.empty-state {
	text-align: center;
	padding: 60px 20px;
	color: #999;
}

.empty-state-icon {
	font-size: 48px;
	margin-bottom: 16px;
	opacity: 0.3;
}

.empty-state-text {
	font-size: 16px;
	margin: 0;
}

.pagination-container {
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 20px;
	margin-top: 30px;
	padding-top: 20px;
	border-top: 1px solid #f0f0f0;
}

.pagination-controls {
	min-width: 100px;
	width: 100px;
}

.card-title {
	margin: 0;
	font-size: 18px;
	font-weight: 600;
	color: #333;
}

.fa-spin {
	animation: fa-spin 2s infinite linear;
}

@keyframes fa-spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

@media (max-width: 768px) {
	.product-card {
		margin-bottom: 16px;
	}

	.action-btn {
		width: 28px;
		height: 28px;
		font-size: 12px;
	}
}

</style>
